import * as React from 'react'
import {
    Grid,
    FormGroup,
    FormControlLabel,
    Radio,
    Select,
    MenuItem,
    Box,
    Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import * as moment from 'moment'
import { toMomentLocaleForPicker } from '@/shared/ui/date/kedoLocale'
import { compactDatePickerLayoutSx } from '@/shared/ui/styles'

type Props = {
    kedo: any
    state: any
    setPartialState: (updates: any) => void
}

export const StepTwo = ({ kedo, state, setPartialState }: Props) => {
    const localeCode = toMomentLocaleForPicker(
        kedo.env().getSelectedLocale()?.code
    )
    const shareUntilMoment = moment(state.shareUntilDate, 'DD-MM-YYYY', true)

    const secureKedocxs = state.kedocxs.filter(
        (item) =>
            Array.isArray(state.secureShareSettings?.kedocx) &&
            state.secureShareSettings.kedocx.includes(item.id)
    )

    return (
        <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {kedo.t('step_two_title', { ns: 'secureshare' })}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {kedo.t('step_two_description', { ns: 'secureshare' })}
                </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ mb: 3 }}>
                    <Box
                        component="h3"
                        sx={{ fontSize: '1rem', fontWeight: 600, mb: 2 }}
                    >
                        {kedo.t('What do you want to share?', {
                            ns: 'secureshare',
                        })}
                    </Box>
                    <FormGroup>
                        <FormControlLabel
                            sx={{
                                m: 0,
                                '& .MuiFormControlLabel-label': { m: 0 },
                            }}
                            control={
                                <Radio
                                    name="share_type"
                                    value="positions"
                                    size="small"
                                    checked={state.shareType === 'positions'}
                                    onChange={() =>
                                        setPartialState({
                                            shareType: 'positions',
                                        })
                                    }
                                />
                            }
                            label={kedo.t('Data position of this dossier', {
                                ns: 'secureshare',
                            })}
                        />
                        <FormControlLabel
                            sx={{
                                m: 0,
                                '& .MuiFormControlLabel-label': { m: 0 },
                            }}
                            control={
                                <Radio
                                    name="share_type"
                                    value="kedocx"
                                    size="small"
                                    checked={state.shareType === 'kedocx'}
                                    disabled={!state.hasKedocxs}
                                    onChange={() =>
                                        setPartialState({ shareType: 'kedocx' })
                                    }
                                />
                            }
                            label={kedo.t('Template (kedocx)', {
                                ns: 'secureshare',
                            })}
                        />
                    </FormGroup>

                    {state.shareType === 'positions' && (
                        <Box sx={{ mt: 2 }}>
                            <Select
                                size="small"
                                fullWidth
                                value={state.shareTypeValue}
                                onChange={(e) =>
                                    setPartialState({
                                        shareTypeValue: e.target.value,
                                    })
                                }
                            >
                                <MenuItem value="">
                                    {kedo.t('Choose an option')}
                                </MenuItem>
                                {state.displayPositions.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                        {kedo.translateItem(
                                            item,
                                            'displayposition'
                                        )}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    )}

                    {state.shareType === 'kedocx' && (
                        <Box sx={{ mt: 2 }}>
                            <Select
                                fullWidth
                                value={state.shareTypeValue}
                                onChange={(e) =>
                                    setPartialState({
                                        shareTypeValue: e.target.value,
                                    })
                                }
                            >
                                <MenuItem value="">
                                    {kedo.t('Choose an option')}
                                </MenuItem>
                                {secureKedocxs.map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                        {item.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    )}
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Box
                        component="h3"
                        sx={{ fontSize: '1rem', fontWeight: 600, mb: 2 }}
                    >
                        {kedo.t('How long is the link valid?', {
                            ns: 'secureshare',
                        })}
                    </Box>
                    <LocalizationProvider
                        dateAdapter={AdapterMoment}
                        adapterLocale={localeCode}
                    >
                        <DatePicker
                            value={
                                shareUntilMoment.isValid()
                                    ? shareUntilMoment
                                    : null
                            }
                            onChange={(m) =>
                                setPartialState({
                                    shareUntilDate:
                                        m && m.isValid()
                                            ? m.format('DD-MM-YYYY')
                                            : '',
                                })
                            }
                            format="DD-MM-YYYY"
                            views={['year', 'month', 'day']}
                            shouldDisableDate={(day) =>
                                !day.isAfter(moment(), 'day')
                            }
                            slotProps={{
                                layout: {
                                    sx: compactDatePickerLayoutSx,
                                },
                                textField: {
                                    size: 'small',
                                    fullWidth: true,
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Box>
            </Grid>
        </Grid>
    )
}
